import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { RuleBicolor } from '@audiq/ui';
import { FlowCreateSchema, type FlowCreate, type Persona } from '@audiq/shared';
import { api } from '../lib/api';
import { ApiClientError } from '../lib/api/_request';
import { PersonaPreviewCard } from '../components/PersonaPreviewCard';

const DEFAULT_PERSONA: Persona = {
  name: 'Audiq',
  role: 'operatore di accoglienza',
  tone: 'caldo',
  formality: 'auto',
  use_emoji: 'mai',
  opening_message: '',
  closing_message: '',
};

const DEFAULT_SETTINGS: FlowCreate['settings'] = {
  ask_personal_info: {
    enabled: true,
    fields: ['first_name', 'last_name'],
    timing: 'at_start',
    required: false,
  },
  use_name_in_conversation: true,
  formality: 'auto',
  max_turns: 50,
  idle_timeout_minutes: 15,
  allow_skip: true,
  emergency_exit: {
    enabled: true,
    keywords: ['dolore al petto', 'difficoltà respiratoria grave', 'perdita coscienza'],
    contact_text: 'Se è una situazione urgente, chiama il 112.',
  },
};

const DEFAULT_VALUES: FlowCreate = {
  name: '',
  slug: '',
  language_default: 'it',
  persona: DEFAULT_PERSONA,
  boundaries: [],
  settings: DEFAULT_SETTINGS,
};

export function FlowsNewPage() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<FlowCreate>({
    resolver: zodResolver(FlowCreateSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const persona = form.watch('persona');
  const flowName = form.watch('name');

  const createMut = useMutation({
    mutationFn: (data: FlowCreate) => api.createFlow(data),
    onSuccess: (newFlow) => {
      navigate(`/flows/${newFlow.id}?tab=questions`);
    },
    onError: (err) => {
      if (err instanceof ApiClientError) {
        setServerError(err.payload.error);
      } else {
        setServerError('Errore di rete');
      }
    },
  });

  function onSubmit(data: FlowCreate) {
    setServerError(null);
    createMut.mutate(data);
  }

  function autoSlug() {
    const name = form.getValues('name');
    const slug = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
    form.setValue('slug', slug, { shouldValidate: true });
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
        Configurazione · Nuovo flow
      </div>
      <h2 className="serif text-4xl" style={{ color: 'var(--ink)' }}>
        Crea un nuovo flow
      </h2>
      <p className="mt-2 max-w-2xl" style={{ color: 'var(--mute)' }}>
        Definisci nome, persona e regole del bot. Le domande e gli esiti li configuri al passaggio
        successivo.
      </p>
      <RuleBicolor className="my-6" />

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]"
      >
        <div className="space-y-5">
          {/* Nome + slug */}
          <div
            className="rounded-md border p-5"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="mono mb-3" style={{ color: 'var(--mute)' }}>
              Identità del flow
            </h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Nome</label>
                <input
                  type="text"
                  {...form.register('name')}
                  onBlur={() => {
                    if (!form.getValues('slug')) autoSlug();
                  }}
                  placeholder="Es. Pre-triage medico generico"
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                />
                {form.formState.errors.name && (
                  <p className="mt-1 text-xs" style={{ color: 'var(--danger)' }}>
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Slug{' '}
                  <span className="mono" style={{ color: 'var(--mute)' }}>
                    (lowercase, trattini)
                  </span>
                </label>
                <input
                  type="text"
                  {...form.register('slug')}
                  placeholder="pre-triage-medico-generico"
                  className="w-full rounded-md border px-3 py-2 font-mono text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                />
                {form.formState.errors.slug && (
                  <p className="mt-1 text-xs" style={{ color: 'var(--danger)' }}>
                    {form.formState.errors.slug.message}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Lingua di default</label>
                <select
                  {...form.register('language_default')}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                >
                  <option value="it">Italiano</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                  <option value="es">Español</option>
                  <option value="ar">العربية</option>
                </select>
              </div>
            </div>
          </div>

          {/* Persona */}
          <div
            className="rounded-md border p-5"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="mono mb-3" style={{ color: 'var(--mute)' }}>
              Persona del bot
            </h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium">Nome</label>
                <input
                  type="text"
                  {...form.register('persona.name')}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Ruolo</label>
                <input
                  type="text"
                  {...form.register('persona.role')}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Tono</label>
                <select
                  {...form.register('persona.tone')}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                >
                  <option value="caldo">Caldo</option>
                  <option value="professionale">Professionale</option>
                  <option value="rassicurante">Rassicurante</option>
                  <option value="neutro">Neutro</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Formality</label>
                <select
                  {...form.register('persona.formality')}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                >
                  <option value="auto">Auto (adattivo)</option>
                  <option value="tu">Tu</option>
                  <option value="lei">Lei</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Emoji</label>
                <select
                  {...form.register('persona.use_emoji')}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                >
                  <option value="mai">Mai</option>
                  <option value="raro">Raro</option>
                  <option value="moderato">Moderato</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium">
                  Apertura{' '}
                  <span className="mono" style={{ color: 'var(--mute)' }}>
                    (opzionale)
                  </span>
                </label>
                <textarea
                  {...form.register('persona.opening_message')}
                  rows={2}
                  placeholder="Lascia vuoto per usare default."
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar: live preview */}
        <aside className="space-y-4">
          <PersonaPreviewCard persona={persona} flowName={flowName} />

          <button
            type="submit"
            disabled={createMut.isPending}
            className="w-full rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-50"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            {createMut.isPending ? 'Creazione…' : 'Crea flow e configura domande'}
          </button>
          {serverError && (
            <p className="text-sm" style={{ color: 'var(--danger)' }}>
              {serverError}
            </p>
          )}
        </aside>
      </form>
    </div>
  );
}
